import { useMemo, useState } from "react";
import { router } from "@inertiajs/react";
import Footer from "./Component/Footer";
import Header from "./Component/Header";

const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(value) || 0);

const formatCoin = (value) =>
    new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Number(value) || 0);

const calculateBonusCoin = (earnedCoin, bonusValue, bonusType) => {
    const totalEarnedCoin = Number(earnedCoin) || 0;
    const normalizedBonusValue = Number(bonusValue) || 0;

    if (Number(bonusType) === 0) {
        return (totalEarnedCoin * normalizedBonusValue) / 100;
    }

    return normalizedBonusValue;
};

const carouselBackgrounds = [
    "https://images.unsplash.com/photo-1630609083938-3acb39a06392?q=80&w=480&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1629194893765-3a904e9080dd?q=80&w=480&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1703587820365-dad81a7c5908?q=80&w=480&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1629196613836-0a7e2541990a?q=80&w=480&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1630609084037-ddb2b4a3fa67?q=80&w=480&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1629654858857-615c2c8be8a8?q=80&w=480&auto=format&fit=crop",
];

export default function Product({ data = [], referralCode = "" }) {
    const products = Array.isArray(data) ? data : [];
    const [selectedProductId, setSelectedProductId] = useState(
        products[0]?.prod_id ?? null
    );
    const hasReferralCode = String(referralCode || "").trim() !== "";

    const selectedProduct = useMemo(
        () =>
            products.find((product) => product.prod_id === selectedProductId) ??
            products[0] ??
            null,
        [products, selectedProductId]
    );
    const selectedVariants = selectedProduct?.variants ?? [];

    const buildReferralLink = () => {
        const params = new URLSearchParams({
            agent_code: String(referralCode ?? "").trim().toUpperCase(),
        });

        return `${window.location.origin}/product/checkout?${params.toString()}`;
    };

    const handleCopyReferralLink = async () => {
        const referralLink = buildReferralLink();

        try {
            await navigator.clipboard.writeText(referralLink);
            window.alert("Link referral berhasil disalin.");
        } catch (error) {
            window.prompt("Salin link referral:", referralLink);
        }
    };

    return (
        <>
            <Header />
            <main id="content">
                <div className="pt-10 pb-20 overflow-hidden">
                    <div className="max-w-6xl px-4 sm:px-6 lg:px-8 mx-auto">
                        <div
                            data-hs-carousel='{
                              "loadingClasses": "opacity-0",
                              "slidesQty": {
                                "xs": 1,
                                "sm": 2,
                                "lg": 3
                              },
                              "isDraggable": true
                            }'
                            className="relative"
                        >
                            <div className="mb-5 md:mb-8 grid grid-cols-2 items-center gap-3">
                                <div className="grow">
                                    <h2 className="font-semibold text-xl md:text-2xl text-foreground">
                                        Produk
                                    </h2>
                                </div>

                                <div className="ms-auto flex justify-center items-center gap-x-2">
                                    <button
                                        type="button"
                                        className="hs-carousel-prev hs-carousel-disabled:opacity-50 hs-carousel-disabled:cursor-default hs-carousel-disabled:hover:bg-layer inline-flex justify-center items-center size-8 sm:size-10 bg-layer border border-line-1 text-layer-foreground rounded-full shadow-2xs hover:bg-muted-hover focus:outline-hidden"
                                    >
                                        <span className="text-2xl" aria-hidden="true">
                                            <svg
                                                className="shrink-0 size-5"
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="m15 18-6-6 6-6"></path>
                                            </svg>
                                        </span>
                                        <span className="sr-only">Previous</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="hs-carousel-next hs-carousel-disabled:opacity-50 hs-carousel-disabled:cursor-default hs-carousel-disabled:hover:bg-layer inline-flex justify-center items-center size-8 sm:size-10 bg-layer border border-line-1 text-layer-foreground rounded-full shadow-2xs hover:bg-muted-hover focus:outline-hidden"
                                    >
                                        <span className="sr-only">Next</span>
                                        <span className="text-2xl" aria-hidden="true">
                                            <svg
                                                className="shrink-0 size-5"
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="m9 18 6-6-6-6"></path>
                                            </svg>
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <div className="hs-carousel w-full">
                                <div className="hs-carousel-body flex flex-nowrap gap-3 md:gap-5 transition-transform duration-700 cursor-grab hs-carousel-dragging:transition-none hs-carousel-dragging:cursor-grabbing">
                                    {products.length === 0 ? (
                                        <div className="hs-carousel-slide">
                                                <div className="rounded-xl border border-line-2 bg-layer p-8 text-sm text-muted-foreground-1">
                                                Belum ada produk tersedia.
                                            </div>
                                        </div>
                                    ) : (
                                        products.map((product, index) => {
                                            const variants = product.variants ?? [];
                                            const startingPrice =
                                                variants.length > 0
                                                    ? Math.min(
                                                          ...variants.map(
                                                              (variant) =>
                                                                  Number(variant.prod_var_price) ||
                                                                  0
                                                          )
                                                      )
                                                    : null;
                                            const imageSrc = product.prod_image
                                                ? `/storage/${product.prod_image}`
                                                : "/momo_live.png";
                                            const backgroundImage =
                                                carouselBackgrounds[
                                                    index % carouselBackgrounds.length
                                                ];

                                            return (
                                                <div
                                                    key={product.prod_id}
                                                    className="hs-carousel-slide"
                                                >
                                                    <div
                                                        className={`group overflow-hidden size-full flex flex-col rounded-xl border bg-layer p-3 transition duration-300 hover:-translate-y-1 hover:shadow-xl ${
                                                            selectedProduct?.prod_id ===
                                                            product.prod_id
                                                                ? "border-primary/60 shadow-lg"
                                                                : "border-line-2 hover:border-primary/30"
                                                        }`}
                                                    >
                                                        <div className="relative rounded-xl overflow-hidden">
                                                            <div className="pt-6 ps-6 sm:pt-10 sm:ps-10 relative z-10 overflow-hidden ms-auto">
                                                                <img
                                                                    className="aspect-square size-full object-cover translate-x-2 group-hover:translate-x-0.5 group-hover:scale-[1.02] transition-transform duration-300 border-t-10 border-s-10 border-line-inverse/50 rounded-tl-2xl shadow-xl"
                                                                    src={imageSrc}
                                                                    alt={product.prod_name}
                                                                />
                                                            </div>

                                                            <div className="size-full absolute inset-0 rounded-xl">
                                                                <img
                                                                    className="size-full object-cover object-center rounded-xl opacity-40 group-hover:opacity-55 transition-opacity duration-300"
                                                                    src={backgroundImage}
                                                                    alt="Product card background"
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="pt-4 flex items-center gap-x-5">
                                                            <div className="grow">
                                                                <p className="text-xs sm:text-sm text-muted-foreground-1">
                                                                    {variants.length} variant
                                                                    {variants.length === 1
                                                                        ? ""
                                                                        : "s"}
                                                                </p>

                                                                <h4 className="mt-2 text-sm sm:text-base text-foreground line-clamp-2">
                                                                    {product.prod_name}
                                                                </h4>

                                                                <p className="mt-2 text-xs sm:text-sm text-muted-foreground-1">
                                                                    {startingPrice === null
                                                                        ? "Harga belum diatur"
                                                                        : `Mulai dari ${formatCurrency(
                                                                              startingPrice
                                                                          )}`}
                                                                </p>
                                                            </div>

                                                            <div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        setSelectedProductId(
                                                                            product.prod_id
                                                                        )
                                                                    }
                                                                    className={`py-1.5 px-3 inline-flex items-center justify-center text-xs font-medium rounded-lg border transition ${
                                                                        selectedProduct?.prod_id ===
                                                                        product.prod_id
                                                                            ? "bg-primary text-primary-foreground border-primary"
                                                                            : "bg-layer text-layer-foreground border-line-1 hover:bg-layer-hover"
                                                                    }`}
                                                                >
                                                                    Lihat Detail
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            <div className="mt-8 rounded-2xl border border-line-2 overflow-hidden">
                                <div className="px-4 py-3 border-b border-line-2 bg-layer">
                                    <h3 className="font-semibold text-foreground">
                                        Varian Produk
                                        
                                        {selectedProduct ? ` - ${selectedProduct.prod_name}` : ""}
                                    </h3>
                                    {hasReferralCode && (
                                        <p className="mt-1 text-xs text-muted-foreground-1">
                                            Kode referral: {referralCode}. Salin link varian lalu bagikan agar checkout terbuka dengan kode reseller Anda.
                                        </p>
                                    )}
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-muted">
                                            <tr>
                                                <th className="px-4 py-3 text-left font-medium text-foreground">
                                                    Varian
                                                </th>
                                                <th className="px-4 py-3 text-left font-medium text-foreground">
                                                    Total Coin Diperoleh
                                                </th>
                                                <th className="px-4 py-3 text-left font-medium text-foreground">
                                                    Bonus Coin
                                                </th>
                                                <th className="px-4 py-3 text-left font-medium text-foreground">
                                                    Harga
                                                </th>
                                                <th className="px-4 py-3 text-left font-medium text-foreground">
                                                    Aksi
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedVariants.length === 0 ? (
                                                <tr className="border-t border-line-2">
                                                    <td
                                                        colSpan={5}
                                                        className="px-4 py-4 text-muted-foreground-1"
                                                    >
                                                        Belum ada varian tersedia.
                                                    </td>
                                                </tr>
                                            ) : (
                                                selectedVariants.map((variant) => (
                                                    <tr
                                                        key={variant.prod_var_id}
                                                        className="border-t border-line-2"
                                                    >
                                                        <td className="px-4 py-3 text-foreground">
                                                            <div className="flex items-center gap-2">
                                                                <span>{variant.prod_var_name}</span>
                                                                {Number(variant.prod_var_type) === 1 && (
                                                                    <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                                                        Custom
                                                                        
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-foreground">
                                                            {Number(variant.prod_var_type) === 1
                                                                ? "Linear mengikuti nominal"
                                                                : formatCoin(
                                                                      variant.total_earned_coin
                                                                  )}
                                                        </td>
                                                        <td className="px-4 py-3 text-foreground">
                                                            {Number(variant.prod_var_type) === 1
                                                                ? "0% / 1% / 2%"
                                                                : formatCoin(
                                                                      calculateBonusCoin(
                                                                          variant.total_earned_coin,
                                                                          variant.prod_var_bonus,
                                                                          variant.prod_var_bonus_type
                                                                      )
                                                                  )}
                                                        </td>
                                                        <td className="px-4 py-3 text-foreground">
                                                            {Number(variant.prod_var_type) === 1
                                                                ? "Nominal kustom"
                                                                : formatCurrency(
                                                                      variant.prod_var_price
                                                                  )}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        router.visit(
                                                                            `/product/checkout?product_id=${selectedProduct?.prod_id ?? ""}&variant_id=${variant.prod_var_id}`
                                                                        )
                                                                    }
                                                                    className="py-1.5 px-3 inline-flex items-center justify-center text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary-hover transition"
                                                                >
                                                                    Beli
                                                                </button>
                                                                {hasReferralCode && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={handleCopyReferralLink}
                                                                        className="py-1.5 px-3 inline-flex items-center justify-center text-xs font-medium rounded-lg border border-line-1 bg-layer text-layer-foreground hover:bg-layer-hover transition"
                                                                    >
                                                                        Salin Link Referral
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
}
